import useApi from '../utils/axios-pettitions';
import { useAppSelector } from '../contexts/hooks';
import { newToast } from '../components';
import {
  MaterialControlDTO,
  CreateMaterialControlDTO,
  LoteControlDTO,
  CreateLoteControlDTO,
  ConfiguracionQcDTO,
  CreateConfiguracionQcDTO,
  WestgardRuleDTO,
  RecalculoSigmaQcDTO,
  ProgramacionRecalculoSigmaDTO,
  SigmaPeriodoMasivoQcDTO,
  SigmaPeriodoMasivoRequest,
  HomologacionQcLoteDTO,
  CreateHomologacionQcLoteDTO,
  UpdateHomologacionQcLoteDTO,
  RecepcionQcDTO,
  DetalleCorridaQcDTO,
  FiltroRecepcionQcDTO,
} from '../types/types.qualityControl';

// El backend devuelve 209 (error de validación) con el motivo concreto en messageDetails
// —qué regla no existe, cuántos puntos necesita la ventana—. Ese detalle es el que le sirve
// al usuario; `message` es genérico.
const describeValidationError = (res: { message?: string; messageDetails?: any }): string => {
  const detalle = typeof res.messageDetails === 'string' ? res.messageDetails.trim() : '';
  if (detalle) return detalle;
  return res.message || 'No se pudo guardar la configuración';
};

// internalCode de éxito del HTTP_RESPONSE_STRUCT. Ojo: 200 es error en esta convención.
const ES_EXITO = [101, 105, 107];

// El serializador del backend conserva PascalCase, pero la especificación del endpoint venía
// documentada en camelCase. Se aceptan ambas para que la pantalla no dependa de esa política.
const leer = <T,>(obj: any, pascal: string, defecto: T): T => {
  const camel = pascal.charAt(0).toLowerCase() + pascal.slice(1);
  const valor = obj?.[pascal] ?? obj?.[camel];
  return valor === undefined || valor === null ? defecto : (valor as T);
};

const normalizarSigmaMasivo = (data: any): SigmaPeriodoMasivoQcDTO => {
  const recalculoCrudo = leer<any>(data, 'Recalculo', null);

  return {
    Fecha: leer(data, 'Fecha', ''),
    Activar: leer(data, 'Activar', false),
    SoloSimulacion: leer(data, 'SoloSimulacion', false),
    Evaluadas: Number(leer(data, 'Evaluadas', 0)),
    Cambiadas: Number(leer(data, 'Cambiadas', 0)),
    YaEstaban: Number(leer(data, 'YaEstaban', 0)),
    NoElegibles: Number(leer(data, 'NoElegibles', 0)),
    Advertencias: leer<string[]>(data, 'Advertencias', []),
    PorConfiguracion: leer<any[]>(data, 'PorConfiguracion', []).map((c) => ({
      ConfiguracionId: Number(leer(c, 'ConfiguracionId', 0)),
      AnalitoNombre: leer<string | null>(c, 'AnalitoNombre', null),
      LoteCodigo: leer<string | null>(c, 'LoteCodigo', null),
      EstadoAnterior: Boolean(leer(c, 'EstadoAnterior', false)),
      Elegible: Boolean(leer(c, 'Elegible', false)),
      Motivo: leer<string | null>(c, 'Motivo', null),
      Cambia: Boolean(leer(c, 'Cambia', false)),
      ModoReglas: leer<string | null>(c, 'ModoReglas', null),
    })),
    // Del recálculo encadenado solo se consumen los contadores del resumen. El detalle por
    // configuración se deja tal cual llega: quien lo necesite ya tiene el modal de recálculo.
    Recalculo: recalculoCrudo
      ? ({
          ...recalculoCrudo,
          ConfiguracionesEvaluadas: Number(leer(recalculoCrudo, 'ConfiguracionesEvaluadas', 0)),
          ConSigmaRepresentativo: Number(leer(recalculoCrudo, 'ConSigmaRepresentativo', 0)),
          SinDatosSuficientes: Number(leer(recalculoCrudo, 'SinDatosSuficientes', 0)),
          CambianDeReglas: Number(leer(recalculoCrudo, 'CambianDeReglas', 0)),
          ConSDaRevisar: Number(leer(recalculoCrudo, 'ConSDaRevisar', 0)),
          Advertencias: leer<string[]>(recalculoCrudo, 'Advertencias', []),
        } as RecalculoSigmaQcDTO)
      : null,
  };
};

export const useApiConfigQC = () => {
  const { runApi } = useApi();
  const userId = useAppSelector((state) => state.userInfo.User?.Us_Id);

  //#region Reglas de Westgard

  // Catálogo de reglas soportadas por el motor. No se hardcodea en el frontend: si el
  // backend gana o cambia una regla, el selector la refleja automáticamente.
  // El serializador del backend conserva PascalCase, pero se normalizan ambas grafías
  // para que un cambio de política de nombres no rompa la pantalla.
  const getWestgardRules = async (): Promise<WestgardRuleDTO[]> => {
    return runApi('qualitycontrol/getWestgardRules', 'GET', null, null).then((res) => {
      if (res.internalCode !== 101 || !Array.isArray(res.data)) return [];
      return (res.data as any[]).map((r) => ({
        nombre: r.Nombre ?? r.nombre ?? '',
        descripcion: r.Descripcion ?? r.descripcion ?? '',
        severidad: (r.Severidad ?? r.severidad ?? 'WARNING') as 'RECHAZO' | 'WARNING',
        puntosPreviosRequeridos: Number(r.PuntosPreviosRequeridos ?? r.puntosPreviosRequeridos ?? 0),
        enConjuntoPorDefecto: Boolean(r.EnConjuntoPorDefecto ?? r.enConjuntoPorDefecto ?? false),
      })).filter((r) => r.nombre !== '');
    });
  };

  // Recálculo del Sigma por período. Con soloSimulacion=true no escribe nada: devuelve qué
  // Sigma saldría y qué configuraciones cambiarían de reglas. Es lo que se ofrece primero.
  // No re-evalúa mediciones ya registradas, solo cambia el criterio de las siguientes.
  const recalculateSigma = async (params: { configuracionId?: number | null; sedeId?: number | null; soloSimulacion?: boolean; notificar?: boolean }) => {
    const query: any = { soloSimulacion: params.soloSimulacion ?? false, notificar: params.notificar ?? false };
    if (params.configuracionId != null) query.configuracionId = params.configuracionId;
    if (params.sedeId != null) query.sedeId = params.sedeId;

    return runApi('qualitycontrol/recalculateSigma', 'POST', query, null).then((res) => {
      if (res.internalCode === 101) return res.data as RecalculoSigmaQcDTO;
      newToast('ERROR', describeValidationError(res), 6);
      return null;
    });
  };

  // Activación (o desactivación) masiva del Sigma por período.
  //
  // recalcularDespues va fijo en true y NO se expone como opción: activar sin recalcular deja
  // las configuraciones SIGMA_AUTO evaluando con TODAS las reglas de Westgard hasta el barrido
  // mensual, que es justo lo contrario de lo que busca quien activa esto.
  //
  // La elegibilidad la decide el backend y la reporta por configuración; aquí no se filtra
  // nada, ni siquiera los ids inexistentes, que vuelven con su propio motivo.
  const putSigmaPeriodoMasive = async (params: SigmaPeriodoMasivoRequest): Promise<SigmaPeriodoMasivoQcDTO | null> => {
    const ids = params.configuracionIds && params.configuracionIds.length > 0 ? params.configuracionIds : null;
    const body = {
      activar: params.activar,
      configuracionIds: ids,
      // sedeId solo tiene efecto en el barrido completo; con ids seleccionados el backend lo ignora
      sedeId: ids ? null : params.sedeId ?? null,
      soloSimulacion: params.soloSimulacion ?? false,
      recalcularDespues: params.recalcularDespues ?? true,
    };

    return runApi('qualitycontrol/putSigmaPeriodoMasive', 'POST', null, body).then((res) => {
      if (!ES_EXITO.includes(res.internalCode) || !res.data) {
        newToast('ERROR', describeValidationError(res), 6);
        return null;
      }
      return normalizarSigmaMasivo(res.data);
    });
  };

  // Estado de la programación del recálculo automático. Solo lectura, no dispara nada.
  const getSigmaScheduleStatus = async () => {
    return runApi('qualitycontrol/getSigmaScheduleStatus', 'GET', null, null).then((res) => {
      if (res.internalCode === 101) return res.data as ProgramacionRecalculoSigmaDTO;
      return null;
    });
  };

  //#endregion

  //#region Materiales de Control

  const getMaterials = async (soloActivos: boolean = true) => {
    return runApi('qualitycontrol/getMaterials', 'GET', { soloActivos }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as MaterialControlDTO[];
      }
      return [];
    });
  };

  const getMaterialById = async (id: number) => {
    return runApi('qualitycontrol/getMaterialById', 'GET', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as MaterialControlDTO;
      }
      return null;
    });
  };

  const newMaterial = async (material: Omit<CreateMaterialControlDTO, 'Qcm_Us_Id'>) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return null;
    }

    const data: CreateMaterialControlDTO = {
      ...material,
      Qcm_Us_Id: userId,
    };

    return runApi('qualitycontrol/newMaterial', 'POST', null, data).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 105) {
        newToast('SUCCESS', res.message, 3);
        return res.data as MaterialControlDTO;
      }
      return null;
    });
  };

  const putMaterial = async (id: number, material: Partial<CreateMaterialControlDTO>) => {
    return runApi('qualitycontrol/putMaterial', 'POST', { id }, material).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return null;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return res.data as MaterialControlDTO;
      }
      return null;
    });
  };

  const deleteMaterial = async (id: number) => {
    return runApi('qualitycontrol/deleteMaterial', 'POST', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return false;
      } else if (res.internalCode === 107 || res.internalCode === 108) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  //#endregion

  //#region Lotes de Control

  const getLotsByMaterial = async (materialId: number, soloActivos: boolean = true) => {
    return runApi('qualitycontrol/getLotsByMaterial', 'GET', { materialId, soloActivos }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as LoteControlDTO[];
      }
      return [];
    });
  };

  const getLotById = async (id: number) => {
    return runApi('qualitycontrol/getLotById', 'GET', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as LoteControlDTO;
      }
      return null;
    });
  };

  const newLot = async (lote: Omit<CreateLoteControlDTO, 'Qcl_Us_Id'>) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return null;
    }

    const data: CreateLoteControlDTO = {
      ...lote,
      Qcl_Us_Id: userId,
    };

    return runApi('qualitycontrol/newLot', 'POST', null, data).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return null;
      } else if (res.internalCode === 105) {
        newToast('SUCCESS', res.message, 3);
        return res.data as LoteControlDTO;
      }
      return null;
    });
  };

  const putLot = async (id: number, lote: Partial<CreateLoteControlDTO>) => {
    return runApi('qualitycontrol/putLot', 'POST', { id }, lote).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return null;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return res.data as LoteControlDTO;
      }
      return null;
    });
  };

  const deleteLot = async (id: number) => {
    return runApi('qualitycontrol/deleteLot', 'POST', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return false;
      } else if (res.internalCode === 107 || res.internalCode === 108) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  //#endregion

  //#region Configuraciones QC

  const getConfigurationsByLot = async (loteId: number, soloVigentes: boolean = true) => {
    return runApi('qualitycontrol/getConfigurationsByLot', 'GET', { loteId, soloVigentes }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO[];
      }
      return [];
    });
  };

  const getConfigurationsBySede = async (sedeId: number, soloVigentes: boolean = true) => {
    return runApi('qualitycontrol/getConfigurationsBySede', 'GET', { sedeId, soloVigentes }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO[];
      }
      return [];
    });
  };

  const getConfigurationById = async (id: number) => {
    return runApi('qualitycontrol/getConfigurationById', 'GET', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO;
      }
      return null;
    });
  };

  const getActiveConfiguration = async (loteId: number, analitoId: number, sedeId: number) => {
    return runApi('qualitycontrol/getActiveConfiguration', 'GET', { loteId, analitoId, sedeId }, null).then((res) => {
      if (res.internalCode >= 200) {
        return null;
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO;
      }
      return null;
    });
  };

  const newConfiguration = async (configuracion: Omit<CreateConfiguracionQcDTO, 'Qcc_Us_Id'>) => {
    if (!userId) {
      newToast('ERROR', 'No se pudo identificar el usuario', 5);
      return null;
    }

    const data: CreateConfiguracionQcDTO = {
      ...configuracion,
      Qcc_Us_Id: userId,
    };

    return runApi('qualitycontrol/newConfiguration', 'POST', null, data).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', describeValidationError(res), 6);
        return null;
      } else if (res.internalCode === 105) {
        newToast('SUCCESS', res.message, 3);
        return res.data as ConfiguracionQcDTO;
      }
      return null;
    });
  };

  const putConfiguration = async (id: number, configuracion: Partial<CreateConfiguracionQcDTO>) => {
    return runApi('qualitycontrol/putConfiguration', 'POST', { id }, configuracion).then((res) => {
      if (res.internalCode >= 200) {
        // En 209 el motivo real (regla inexistente, ventana insuficiente...) viaja en messageDetails
        newToast('ERROR', describeValidationError(res), 6);
        return null;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return res.data as ConfiguracionQcDTO;
      }
      return null;
    });
  };

  const endConfigurationValidity = async (id: number) => {
    return runApi('qualitycontrol/endConfigurationValidity', 'POST', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        return false;
      } else if (res.internalCode === 107) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  const deleteConfiguration = async (id: number) => {
    return runApi('qualitycontrol/deleteConfiguration', 'POST', { id }, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message, 5);
        return false;
      } else if (res.internalCode === 107 || res.internalCode === 108) {
        newToast('SUCCESS', res.message, 3);
        return true;
      }
      return false;
    });
  };

  //#endregion

  //#region Endpoints consolidados (evita N+1 desde el frontend)

  const getAllLots = async (soloActivos: boolean = true) => {
    return runApi('qualitycontrol/getAllLots', 'GET', { soloActivos }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as LoteControlDTO[];
      }
      return [];
    });
  };

  const getAllConfigurations = async (soloVigentes: boolean = true) => {
    return runApi('qualitycontrol/getAllConfigurations', 'GET', { soloVigentes }, null).then((res) => {
      if (res.internalCode >= 200) {
        return [];
      } else if (res.internalCode === 101) {
        return res.data as ConfiguracionQcDTO[];
      }
      return [];
    });
  };

  //#endregion

  //#region Homologación de controles por analizador

  // Qué identificador reporta cada analizador y a qué material de control corresponde.
  // Sin al menos una fila aquí, todo lo que mande el equipo queda en la bandeja de recepción
  // sin registrar: es la tabla que traduce.
  const getQcLotHomologations = async (analizadorId?: number | null, soloActivas: boolean = true): Promise<HomologacionQcLoteDTO[]> => {
    const query: any = { soloActivas };
    if (analizadorId != null) query.analizadorId = analizadorId;

    return runApi('qualitycontrol/getQcLotHomologations', 'GET', query, null).then((res) => {
      if (res.internalCode !== 101 || !Array.isArray(res.data)) return [];
      return res.data as HomologacionQcLoteDTO[];
    });
  };

  // El backend exige exactamente un destino (lote o material) y rechaza el duplicado sobre
  // (analizador, código) con 209. El motivo concreto viaja en messageDetails, así que se
  // muestra ese y no el mensaje genérico.
  const newQcLotHomologation = async (dto: CreateHomologacionQcLoteDTO): Promise<HomologacionQcLoteDTO | null> => {
    return runApi('qualitycontrol/postQcLotHomologation', 'POST', null, dto).then((res) => {
      if (!ES_EXITO.includes(res.internalCode) || !res.data) {
        newToast('ERROR', describeValidationError(res), 6);
        return null;
      }
      return res.data as HomologacionQcLoteDTO;
    });
  };

  const putQcLotHomologation = async (dto: UpdateHomologacionQcLoteDTO): Promise<HomologacionQcLoteDTO | null> => {
    return runApi('qualitycontrol/putQcLotHomologation', 'POST', null, dto).then((res) => {
      if (!ES_EXITO.includes(res.internalCode) || !res.data) {
        newToast('ERROR', describeValidationError(res), 6);
        return null;
      }
      return res.data as HomologacionQcLoteDTO;
    });
  };

  // Corridas recibidas de los analizadores, homologadas o no. Es lo que responde
  // "por qué no está entrando este control".
  const getQcReceptions = async (filtro: FiltroRecepcionQcDTO): Promise<RecepcionQcDTO[]> => {
    return runApi('qualitycontrol/getQcReceptions', 'POST', null, filtro ?? {}).then((res) => {
      if (res.internalCode !== 101 || !Array.isArray(res.data)) return [];
      return res.data as RecepcionQcDTO[];
    });
  };

  // Contra qué apunta cada código de una corrida: analito homologado y configuración QC.
  // Se pide al desplegar una fila, no al listar. El backend lo resuelve con cuatro consultas
  // fijas, así que el costo no crece con el número de analitos de la corrida.
  const getQcRunDetail = async (recepcionId: number): Promise<DetalleCorridaQcDTO[]> => {
    return runApi('qualitycontrol/getQcRunDetail', 'POST', { recepcionId }, null).then((res) => {
      if (res.internalCode !== 101 || !Array.isArray(res.data)) return [];
      return res.data as DetalleCorridaQcDTO[];
    });
  };

  // Reintenta una corrida con la copia cruda que quedó guardada: no hay que pedirle nada al
  // analizador. El backend rechaza reprocesar una corrida ya PROCESADA, porque duplicaría
  // sus mediciones.
  const reprocessQcReception = async (recepcionId: number): Promise<boolean> => {
    return runApi('qualitycontrol/reprocessQcReception', 'POST', { recepcionId }, null).then((res) => {
      if (res.internalCode !== 101) {
        newToast('ERROR', describeValidationError(res), 6);
        return false;
      }
      return true;
    });
  };

  //#endregion

  return {

    getWestgardRules,
    recalculateSigma,
    putSigmaPeriodoMasive,
    getSigmaScheduleStatus,
    getMaterials,
    getMaterialById,
    newMaterial,
    putMaterial,
    deleteMaterial,
    getAllLots,
    getLotsByMaterial,
    getLotById,
    newLot,
    putLot,
    deleteLot,
    getAllConfigurations,
    getConfigurationsByLot,
    getConfigurationsBySede,
    getConfigurationById,
    getActiveConfiguration,
    newConfiguration,
    putConfiguration,
    endConfigurationValidity,
    deleteConfiguration,
    getQcLotHomologations,
    newQcLotHomologation,
    putQcLotHomologation,
    getQcReceptions,
    getQcRunDetail,
    reprocessQcReception,
  };
};
